import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Variant switcher on a cart drawer line (23/09/2026).
 *
 * Shopify cannot change the variant of an existing line, so the swap goes through /cart/update.js
 * keyed by variant id: the old variant goes to 0 and the new one takes this line's quantity (plus
 * whatever the cart already holds of it, so an existing line is topped up instead of overwritten).
 * The lines carry no line properties (the bundle block adds plain variant lines), which is what
 * makes the by-variant form safe. The drawer is morphed from the section that comes back with the
 * response, the same way st-shipping-protection.js does it.
 *
 * The <select> lives inside the morphed section, so the listener is delegated on the document and
 * registered only once.
 */
if (!window.__stCartVariant) {
  window.__stCartVariant = true;

  // Playbook §6.3: count every cart change so a late response never repaints over a newer state.
  let cartMutationSeq = 0;
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    cartMutationSeq++;
  });

  let busy = false;

  document.addEventListener('change', async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    const select = target.closest('.st-line__variant');
    if (!(select instanceof HTMLSelectElement)) return;

    const from = select.dataset.variantId || '';
    const to = select.value;
    if (!from || !to || from === to) return;

    if (busy) {
      select.value = from;
      return;
    }

    const quantity = parseInt(select.dataset.lineQty || '1', 10) || 1;
    const inCart = parseInt(select.selectedOptions[0]?.dataset.cartQty || '0', 10) || 0;
    const host = select.closest('cart-items-component');
    const sectionId = host instanceof HTMLElement ? host.dataset.sectionId || '' : '';

    busy = true;
    select.disabled = true;
    host?.classList.add('cart-items-disabled');

    try {
      const body = JSON.stringify({
        updates: { [from]: 0, [to]: inCart + quantity },
        ...(sectionId ? { sections: [sectionId], sections_url: window.location.pathname } : {}),
      });
      const seqAtStart = cartMutationSeq;
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || data?.message || 'Cart update failed');

      // a newer cart change started meanwhile: announce the cart, but let that change paint the drawer
      if (cartMutationSeq !== seqAtStart) data.sections = undefined;

      select.dispatchEvent(
        new CartUpdateEvent(data, sectionId || 'st-cart-variant', {
          source: 'st-cart-variant',
          itemCount: data.item_count,
          sections: data.sections,
        })
      );
    } catch (error) {
      select.value = from;
      console.warn('[st-cart-variant]', error);
    } finally {
      busy = false;
      select.disabled = false;
      host?.classList.remove('cart-items-disabled');
    }
  });
}
